import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import Modal from "components/Modal";
import BlacklistTable from "./components/BlacklistTable";
import BlacklistFilters from "./components/BlacklistFilters";
import BlacklistForm from "./components/BlacklistForm";
import EditReasonModal from "./components/EditReasonModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import EmptyState from "./components/EmptyState";

import {
  fetchBlacklistEntries,
  addBlacklistEntry,
  updateBlacklistEntry,
  deleteBlacklistEntry,
  fetchAvailableContacts,
  setBlacklistFilters,
  clearBlacklistError,
} from "../../../actions/contactBlacklist";

import { setMessage } from "../../../actions/message";

import styles from "./ContactBlacklist.module.scss";

const ContactBlacklist = () => {
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: 'created_at', direction: 'desc' });

  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useSelector((state) => state.permissions);
  const {
    entries,
    contacts,
    filters,
    operations
  } = useSelector((state) => state.contactBlacklist);

  // Reset to list view when user navigates to this page (even from the same URL)
  useEffect(() => {
    setShowForm(false);
    setShowEditForm(false);
    setEditingEntry(null);
  }, [location.key]);

  // Check permissions
  const hasReadAccess = permissions?.rfq_notification?.manage_rfq_notifications || permissions?.rfq_notification?.manage_rfq_contact_blacklist;
  const hasWriteAccess = permissions?.rfq_notification?.manage_rfq_contact_blacklist;

  const loadBlacklistData = useCallback(async (currentFilters = filters, page = 1, pageSize = 25) => {
    try {
      await dispatch(fetchBlacklistEntries(currentFilters, page, pageSize));
    } catch (error) {
      console.error('Error loading blacklist data:', error);
    }
  }, [dispatch, filters]);

  useEffect(() => {
    if (hasReadAccess) {
      loadBlacklistData();
    }
  }, [hasReadAccess, loadBlacklistData]);

  const handleContactsSearch = useCallback(async (query) => {
    try {
      await dispatch(fetchAvailableContacts(query));
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  }, [dispatch]);

  const handleAddEntry = () => {
    if (!hasWriteAccess) return;
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEditEntry = async (entry) => {
    if (!hasWriteAccess) return;
    setEditingEntry(entry);
    
    // Load contacts if we have a contact email to search for
    if (entry.blacklist_type === 'contact' && entry.contact_email) {
      try {
        await handleContactsSearch(entry.contact_email);
      } catch (error) {
        console.error('Failed to load contacts for edit:', error);
      }
    }
    
    setShowEditForm(true);
  };

  const handleDeleteEntry = (entry) => {
    if (!hasWriteAccess) return;
    setSelectedEntry(entry);
    setShowDeleteModal(true);
  };

  const handleFormSave = async (entryData) => {
    try {
      if (editingEntry) {
        await dispatch(updateBlacklistEntry(editingEntry.id, entryData));
        dispatch(setMessage({ 
          type: "success", 
          text: "Blacklist entry updated successfully" 
        }));
      } else {
        await dispatch(addBlacklistEntry(entryData));
        dispatch(setMessage({ 
          type: "success", 
          text: "Entry added to blacklist successfully" 
        }));
      }
      setShowForm(false);
      setEditingEntry(null);
    } catch (error) {
      throw new Error(error.message || "Failed to save blacklist entry");
    }
  };

  const handleEditFormSave = async (updateData) => {
    try {
      await dispatch(updateBlacklistEntry(editingEntry.id, updateData));
      setShowEditForm(false);
      setEditingEntry(null);
      dispatch(setMessage({ 
        type: "success", 
        text: "Blacklist entry updated successfully" 
      }));
    } catch (error) {
      dispatch(setMessage({ 
        type: "error", 
        text: error.message || "Failed to update blacklist entry" 
      }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteBlacklistEntry(selectedEntry.id));
      setShowDeleteModal(false);
      setSelectedEntry(null);
      dispatch(setMessage({ 
        type: "success", 
        text: "Entry removed from blacklist successfully" 
      }));
    } catch (error) {
      dispatch(setMessage({ 
        type: "error", 
        text: error.message || "Failed to remove entry from blacklist" 
      }));
    }
  };

  const handleFiltersChange = (newFilters) => {
    dispatch(setBlacklistFilters(newFilters));
  };

  const handleSort = (field) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field, direction });
  };

  const handlePageChange = (newPage) => {
    loadBlacklistData(filters, newPage, entries.pagination.pageSize);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const handleEditFormCancel = () => {
    setShowEditForm(false);
    setEditingEntry(null);
  };

  const closeModals = () => {
    setShowDeleteModal(false);
    setSelectedEntry(null);
    dispatch(clearBlacklistError());
  };

  // Sort entries locally if needed
  const sortedEntries = [...entries.data].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const hasData = entries.data && entries.data.length > 0;

  // Show form view when showForm is true
  if (showForm) {
    return (
      <div className={styles.wrapper}>
        <BlacklistForm
          entry={editingEntry}
          loading={operations.adding || operations.updating}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          contacts={contacts}
          onContactsSearch={handleContactsSearch}
        />
      </div>
    );
  }

  // Show edit form when showEditForm is true
  if (showEditForm) {
    return (
      <div className={styles.wrapper}>
        <EditReasonModal
          entry={editingEntry}
          contacts={contacts}
          onSave={handleEditFormSave}
          onCancel={handleEditFormCancel}
          loading={operations.updating}
          error={operations.error}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <BlacklistFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              loading={entries.loading}
            />
          </div>
          <div className={styles.headerRight}>
            {hasWriteAccess && (
              <button
                className={styles.addButton}
                onClick={handleAddEntry}
                disabled={entries.loading}
              >
                Add to Blacklist
              </button>
            )}
          </div>
        </div>

        {entries.loading && !hasData ? (
          <div className={styles.loading}>Loading blacklist entries...</div>
        ) : entries.error ? (
          <div className={styles.error}>
            Error loading blacklist: {entries.error}
            <button 
              className={styles.retryButton}
              onClick={() => loadBlacklistData()}
            >
              Retry
            </button>
          </div>
        ) : hasData ? (
          <BlacklistTable
            entries={sortedEntries}
            sortConfig={sortConfig}
            onSort={handleSort}
            onEdit={handleEditEntry}
            onDelete={handleDeleteEntry}
            hasWriteAccess={hasWriteAccess}
            loading={entries.loading}
            pagination={entries.pagination}
            onPageChange={handlePageChange}
          />
        ) : (
          <EmptyState
            hasWriteAccess={hasWriteAccess}
            onAddFirst={handleAddEntry}
            hasFilters={filters.search || filters.type !== 'all'}
          />
        )}
      </div>

      {/* Modals */}
      {showDeleteModal && selectedEntry && hasWriteAccess && (
        <Modal
          isOpen={showDeleteModal}
          onClose={closeModals}
          hideCloseBtn={true}
          content={
            <DeleteConfirmModal
              isOpen={showDeleteModal}
              entry={selectedEntry}
              onConfirm={handleDeleteConfirm}
              onClose={closeModals}
              loading={operations.deleting}
            />
          }
        />
      )}
    </div>
  );
};

export default ContactBlacklist;